/**
 * Browser Web Push: register the service worker, run the permission +
 * subscribe handshake, and keep the backend's subscription row in sync.
 *
 * This is the real notification transport now that the app is web-first.
 * Everything no-ops gracefully when the browser lacks support (older Safari,
 * a plain iOS Safari tab that isn't an installed PWA) so callers can render a
 * single honest state instead of pretending notifications are on.
 *
 * The product rule: enabling notifications turns *every* category on (the
 * backend does this when the subscription lands); the user then disables
 * individual categories from the settings toggles.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  deletePushSubscription,
  getVapidKey,
  savePushSubscription,
  type SubscribeResult,
} from '@/api/services';
import { logger } from './logger';

const SW_URL = '/sw.js';

export type WebPushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

export function isWebPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPermission(): WebPushPermission {
  if (!isWebPushSupported()) return 'unsupported';
  return Notification.permission as WebPushPermission;
}

/** The browser hands back a base64url VAPID key; PushManager wants a
 * Uint8Array. Standard conversion. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  // register() is idempotent — returns the existing registration if the SW is
  // already controlling this scope.
  const reg = await navigator.serviceWorker.register(SW_URL);
  await navigator.serviceWorker.ready;
  return reg;
}

async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isWebPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch (err) {
    logger.warn('webPush.getExistingSubscription failed', { err: String(err) });
    return null;
  }
}

function toBody(sub: PushSubscription) {
  // PushSubscription.toJSON() → { endpoint, keys: { p256dh, auth } }.
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  return {
    endpoint: json.endpoint ?? sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}

export interface EnableResult {
  ok: boolean;
  permission: WebPushPermission;
  /** Present when ok — the backend flipped every category on; callers should
   * adopt these so the toggles render correctly. */
  preferences?: SubscribeResult['notificationPreferences'];
}

/**
 * Full opt-in: register the SW, prompt for permission, subscribe to push, and
 * persist the subscription. Resolves with the resulting permission so the UI
 * can show the right state on denial.
 */
export async function enableWebPush(): Promise<EnableResult> {
  if (!isWebPushSupported()) return { ok: false, permission: 'unsupported' };

  // Fetch the VAPID key first — if web push isn't configured server-side this
  // 503s and we bail before bothering the user with a permission prompt.
  let publicKey: string;
  try {
    publicKey = (await getVapidKey()).publicKey;
  } catch (err) {
    logger.warn('webPush.enable: no VAPID key', { err: String(err) });
    return { ok: false, permission: getPermission() };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, permission: permission as WebPushPermission };
  }

  try {
    const reg = await getRegistration();
    // Reuse an existing subscription if the browser already has one.
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const result = await savePushSubscription(toBody(sub));
    return { ok: true, permission: 'granted', preferences: result.notificationPreferences };
  } catch (err) {
    logger.error('webPush.enable: subscribe failed', err);
    return { ok: false, permission: 'granted' };
  }
}

/** Tear down: unsubscribe locally and delete the backend row. */
export async function disableWebPush(): Promise<void> {
  const sub = await getExistingSubscription();
  if (!sub) return;
  const { endpoint } = toBody(sub);
  try {
    await sub.unsubscribe();
  } catch (err) {
    logger.warn('webPush.disable: local unsubscribe failed', { err: String(err) });
  }
  try {
    await deletePushSubscription(endpoint);
  } catch (err) {
    logger.warn('webPush.disable: backend delete failed', { err: String(err) });
  }
}

export interface UseWebPush {
  supported: boolean;
  permission: WebPushPermission;
  enabled: boolean;
  busy: boolean;
  enable: () => Promise<EnableResult>;
  disable: () => Promise<void>;
}

/**
 * React binding for the settings screen. Tracks support, OS permission, and
 * whether a live subscription exists so the UI never claims notifications are
 * on when they aren't.
 */
export function useWebPush(): UseWebPush {
  const [supported] = useState(isWebPushSupported);
  const [permission, setPermission] = useState<WebPushPermission>(getPermission);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!supported) return;
    void getExistingSubscription().then((sub) => {
      if (cancelled) return;
      setEnabled(!!sub && Notification.permission === 'granted');
      setPermission(getPermission());
    });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async (): Promise<EnableResult> => {
    setBusy(true);
    try {
      const result = await enableWebPush();
      setPermission(result.permission);
      setEnabled(result.ok);
      return result;
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      await disableWebPush();
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, []);

  return { supported, permission, enabled, busy, enable, disable };
}
